import React from "react";
import { useAuth } from "../../../../features/auth/hooks/useAuth";
import { useUILibrary } from "../../../lib/ui-lib/ui-library-context";
import { T } from "../../../../styles/tokens";
import { CommandPalette } from "../../../../features/command-pallete/component/Commandpalette";
import { useNavigate } from "react-router-dom";

export const Header: React.FC = () => {
  const navigate = useNavigate();

  const { user, handleLogout } = useAuth();
  const { Dropdown } = useUILibrary();

  // ── Avatar initials ──────────────────────────────────────────────────────
  const initials = (() => {
    const name = user?.name?.trim();
    if (!name) return "AM";
    const parts = name.split(/\s+/).filter(Boolean);
    return parts
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase();
  })();

  const dropdownItems = {
    items: [
      {
        key: "profile",
        label: "Profile",
        onClick: () => navigate("/account/profile"),
      },
      {
        key: "change-password",
        label: "Change Password",
        onClick: () => navigate("/account/change-password"),
      },
      { type: "divider" as const, key: "divider-1" },
      {
        key: "logout",
        label: "Logout",
        danger: true,
        onClick: async () => {
          await handleLogout();
        },
      },
    ],
  };

  return (
    <header style={s.header}>
      {/* Top row: org name (left) + controls (right) */}
      <div style={s.topRow}>
        <div style={s.orgBadge}>
          <CorporateIcon />
          <span style={s.orgName}>Admin</span>
        </div>

        <div style={s.rightGroup}>
          <CommandPalette />
          <button
            style={s.iconBtn}
            aria-label="Notifications"
            onClick={() => window.open("/routing/security", "_blank")}
          >
            <BellIcon />
            <span style={s.badge} />
          </button>

          {/* Avatar + chevron */}
          <Dropdown
            menu={dropdownItems}
            trigger={["click"]}
            placement="bottomRight"
          >
            <button style={s.avatarBtn}>
              <div style={s.avatar}>{initials}</div>
              <ChevronDownIcon />
            </button>
          </Dropdown>
        </div>
      </div>

      {/* 1px separator — sits outside topRow so it spans full width */}
      <div style={s.separator} />
    </header>
  );
};

/* ── Styles ──────────────────────────────────────────────────────────────── */
export const s: Record<string, React.CSSProperties> = {
  header: {
    position: "sticky",
    top: 0,
    zIndex: 99,
    height: "var(--header-height)" as unknown as number,
    background: "var(--color-bg-header)",
    display: "flex",
    flexDirection: "column", // column so separator sits below the row
    justifyContent: "space-between",
    boxSizing: "border-box",
    width: "100%",
    flexShrink: 0,
    // NO padding here — padding lives in topRow so separator reaches edges
  },
  topRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flex: 1,
    padding: "0 12px", // padding only on the content row
  },
  separator: {
    height: 1,
    margin: "0 12px", // matches topRow padding — gives the gap effect
    background: "var(--color-border)",
    flexShrink: 0,
  },
  orgBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "5px 12px 5px 9px",
    borderRadius: 8,
    background: "var(--color-brand-primary-subtle, rgba(99,102,241,0.08))",
    border:
      "1px solid var(--color-brand-primary-border, rgba(99,102,241,0.18))",
  },
  orgName: {
    fontSize: "var(--text-label-md-size)" as unknown as number,
    fontWeight: 700,
    color: "var(--color-brand-primary)",
    letterSpacing: "-0.01em",
    lineHeight: 1,
  },
  rightGroup: {
    display: "flex",
    alignItems: "center",
    gap: 20,
  },
  playSoundWrap: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  playSoundLabel: {
    fontSize: "var(--text-label-sm-size)" as unknown as number,
    color: "var(--color-text-tertiary)",
    marginRight: 4,
  },
  toggleLabel: {
    fontSize: "var(--text-caption-size)" as unknown as number,
    fontWeight: 600,
    minWidth: 16,
    textAlign: "center" as const,
  },
  iconBtn: {
    position: "relative",
    background: "none",
    border: "none",
    padding: 6,
    cursor: "pointer",
    color: "var(--color-text-tertiary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: T.radius.md,
  },
  badge: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 8,
    height: 8,
    background: "var(--color-brand-primary)",
    borderRadius: "50%",
    border: "2px solid var(--color-bg-header)",
  },
  avatarBtn: {
    background: "none",
    border: "none",
    padding: "4px 6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    borderRadius: T.radius.md,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "var(--color-brand-primary)",
    color: "#fff",
    fontSize: "var(--text-label-sm-size)" as unknown as number,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
};

/* ── SVG Icons ───────────────────────────────────────────────────────────── */
const CorporateIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--color-brand-primary)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M9 21V9" />
  </svg>
);

const BellIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={T.text.placeholder}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
